import React, { useEffect, useState } from 'react';
import Icon from '../components/ui/Icon';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import { usePopup } from '../context/PopupContext';
import { statusMeta, canEdit, canDelete } from '../utils/testStatus';
import './TestsPro.css';

const Tests = () => {
  const navigate = useNavigate();
  const { showConfirm, showAlert } = usePopup();
  const [tests, setTests] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [stats, setStats] = useState({ total: 0, draft: 0, scheduled: 0, active: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Server-side filters, sort and pagination (audit M16); the page holds one page of rows plus whole-scope status counts.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadTick, setReloadTick] = useState(0);
  const itemsPerPage = 10;
  const fetchTests = () => setReloadTick(n => n + 1);

  useEffect(() => { const h = setTimeout(() => setDebouncedSearch(search.trim()), 300); return () => clearTimeout(h); }, [search]);
  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, statusFilter, typeFilter, sortConfig]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    const params = { page: currentPage, limit: itemsPerPage, sort: sortConfig.key, dir: sortConfig.direction };
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusFilter !== 'All') params.status = statusFilter;
    if (typeFilter !== 'All') params.type = typeFilter;
    API.get('/tests', { params })
      .then(res => {
        if (cancelled) return;
        setTests(res.data.data || []); setMeta(res.data.metadata || { total: 0, total_pages: 1 });
        if (res.data.stats) setStats(res.data.stats);
      })
      .catch(err => { if (!cancelled) { console.error(err); setError('Failed to load tests.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentPage, debouncedSearch, statusFilter, typeFilter, sortConfig, reloadTick]);

  const handleDelete = async (test) => {
    if (!canDelete(test)) return;
    const confirmed = await showConfirm('Delete Test', 'Delete this test? All assignments and results will be lost.');
    if (!confirmed) return;
    try {
      await API.delete(`/tests/${test.id}`);
      fetchTests();
      showAlert('Success', 'Test deleted successfully');
    } catch { 
      showAlert('Error', 'Failed to delete test'); 
    }
  };

  const handlePublish = async (test) => {
    const confirmed = await showConfirm('Publish Test', 'Are you sure you want to publish this test? It will become active immediately.');
    if (!confirmed) return;
    try {
      await API.post(`/tests/${test.id}/publish`);
      fetchTests();
      showAlert('Success', 'Test published successfully');
    } catch {
      showAlert('Error', 'Failed to publish test');
    }
  };

  const totalPages = Math.max(1, meta.total_pages || 1);
  const paginatedTests = tests;

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  return (
    <div className="tmx-page">
      <div className="tmx-top">
        <div>
          <h1>Test Management</h1>
          <p>Create, manage, and analyze all your assessments.</p>
        </div>
        <div className="tmx-actions">
          <button className="tmx-btn" onClick={() => navigate('/admin/tests/planner')} aria-label="Plan a new assessment with the Test Planner">
            <Icon name="sparkle" size={16} /> Plan an Assessment
          </button>
          <button className="tmx-btn primary" onClick={() => navigate('/admin/tests/create')} aria-label="Create New Test">
            Create New Test (Manual)
          </button>
        </div>
      </div>

      <div className="tmx-stats">
        <div className="tmx-stat"><div className="v">{stats.total}</div><div className="l">Total Tests</div></div>
        <div className="tmx-stat"><div className="v">{stats.draft}</div><div className="l">Drafts</div></div>
        <div className="tmx-stat"><div className="v">{stats.scheduled}</div><div className="l">Scheduled</div></div>
        <div className="tmx-stat"><div className="v">{stats.active}</div><div className="l">Active</div></div>
        <div className="tmx-stat"><div className="v">{stats.completed}</div><div className="l">Completed</div></div>
      </div>

      <div className="tmx-toolbar">
        <input 
          type="text" 
          placeholder="Search tests by name..." 
          value={search} 
          onChange={e => setSearch(e.target.value)} 
          className="tmx-search" 
        />
        <select className="tmx-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
          <option value="All">Status: All</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
        <select className="tmx-select" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setCurrentPage(1); }}>
          <option value="All">Type: All</option>
          <option value="Aptitude Test">Aptitude</option>
          <option value="Diagnostic Test">Diagnostic</option>
        </select>
        {(search || statusFilter !== 'All' || typeFilter !== 'All') && (
          <button className="tmx-clear" onClick={() => { setSearch(''); setStatusFilter('All'); setTypeFilter('All'); }}>
            Clear Filters
          </button>
        )}
      </div>

      {error && (
        <div className="tmx-state" style={{color: '#dc2626'}}>
          <p>{error}</p>
          <button className="tmx-btn" onClick={fetchTests}>Retry</button>
        </div>
      )}

      {!error && (
        <div className="tmx-table-wrap">
          <table className="tmx-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => requestSort('test_name')}>
                  Test Name {sortConfig.key === 'test_name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable" onClick={() => requestSort('test_type')}>
                  Type {sortConfig.key === 'test_type' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable" onClick={() => requestSort('question_count')}>
                  Questions {sortConfig.key === 'question_count' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th>Duration</th>
                <th className="sortable" onClick={() => requestSort('scheduled_at')}>
                  Scheduled {sortConfig.key === 'scheduled_at' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th>Audience / Completed</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i}>
                    <td colSpan="8"><div className="tmx-skel"></div></td>
                  </tr>
                ))
              ) : paginatedTests.length === 0 ? (
                <tr><td colSpan="8" className="tmx-state">No tests found matching your criteria.</td></tr>
              ) : (
                paginatedTests.map(t => {
                  const sMeta = statusMeta(t.derived_status);
                  const editable = canEdit(t);
                  const deletable = canDelete(t);

                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="tmx-name" onClick={() => navigate(`/admin/tests/${t.id}`)}>{t.test_name}</div>
                        <div style={{fontSize: '0.75rem', color: '#64748b', marginTop: '2px'}}>
                          Created {new Date(t.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td><span className="tmx-pill" style={{background: '#f1f5f9', color: '#334155'}}>{t.test_type || 'General'}</span></td>
                      <td>{t.question_count || 0}</td>
                      <td>{t.duration_minutes ? `${t.duration_minutes} min` : '—'}</td>
                      <td>{t.scheduled_at ? new Date(t.scheduled_at).toLocaleString() : '—'}</td>
                      <td>
                        <div style={{fontSize: '0.8rem', color: '#334155'}} title="Students completed out of the real assigned audience">
                          {t.completed_count || 0} / {t.audience_count || 0}
                        </div>
                      </td>
                      <td>
                        <span className="tmx-badge" style={{ background: sMeta.bg, color: sMeta.color }}>
                          {sMeta.label}
                        </span>
                      </td>
                      <td className="tmx-actions tmx-row-actions">
                        <button 
                          className="tmx-icon-btn" 
                          onClick={() => navigate(`/admin/tests/${t.id}?tab=results`)} 
                          title="View Results" 
                          aria-label="View Results"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
                        </button>
                        
                        {t.derived_status === 'draft' && (
                           <button 
                            className="tmx-icon-btn" 
                            onClick={() => handlePublish(t)} 
                            title="Publish Test" 
                            aria-label="Publish Test"
                           >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                           </button>
                        )}
                        
                        <button 
                          className="tmx-icon-btn" 
                          onClick={() => navigate(`/admin/tests/${t.id}`)} 
                          title="Manage" 
                          aria-label="Manage Test"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                        </button>
                        
                        <button 
                          className="tmx-icon-btn" 
                          onClick={() => navigate(`/admin/tests/edit/${t.id}`)} 
                          title={editable ? "Edit" : "Cannot edit test with submitted attempts"} 
                          aria-label="Edit Test"
                          disabled={!editable}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                        </button>
                        
                        <button 
                          className="tmx-icon-btn del" 
                          onClick={() => handleDelete(t)} 
                          title={deletable ? "Delete" : "Cannot delete test with submitted attempts"} 
                          aria-label="Delete Test"
                          disabled={!deletable}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          
          {totalPages > 1 && (
            <div className="tmx-pagination">
              <span>Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, meta.total)} of {meta.total} tests</span>
              <div className="tmx-page-btns">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Tests;